import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {Link} from 'shells/link/link';
import {LinkedEntity} from 'shells/linked-entity/linked-entity';

import type {ChangeType} from 'nutshell-graphql-types';
import type {
    EventFragmentSparse,
    SessionUser,
    EventFragmentSparse_changes as Change,
    EventFragmentSparse_payload_Quote_lead as Lead,
} from 'nutshell-graphql-types';

import {TimelineBannerLeadValue} from './timeline-banner-lead-value';

import {
    TimelineBannerCompanyType,
    TimelineBannerEntityModified,
    TimelineBannerLeadCloned,
    TimelineBannerLeadPriority,
    TimelineBannerLeadStatus,
    TimelineBannerStageChange,
    TimelineBannerMailchimp,
    TimelineBannerIntelligenceDetailsAdded,
    TimelineBannerEntityMerged,
} from './';

const PRIORITY = 'priority';
const STATUS = 'status';
const STAGE = 'stage';
const VALUE = 'value';
const VALUE_MODE = 'value_mode';
const LEAD_OPENED_STATUS = '0';

export function getContactBanner(input: {changeType: ChangeType; event: EventFragmentSparse}) {
    const {changeType, event} = input;

    if (changeType === 'MERGED') {
        return <TimelineBannerEntityMerged key={event.id} event={event} />;
    }

    if (changeType === 'MAILCHIMP') {
        return <TimelineBannerMailchimp key={event.id} event={event} />;
    }

    if (changeType === 'ENRICHMENT_ACCEPTED') {
        return <TimelineBannerIntelligenceDetailsAdded key={event.id} event={event} />;
    }

    return <TimelineBannerEntityModified key={event.id} event={event} />;
}

export function getAccountBanner(input: {changeType: ChangeType; event: EventFragmentSparse}) {
    const {changeType, event} = input;

    if (changeType === 'ADDED' || changeType === 'DELETED' || changeType === 'UNDELETED') {
        return <TimelineBannerEntityModified key={event.id} event={event} />;
    }

    if (changeType === 'MERGED') {
        return <TimelineBannerEntityMerged key={event.id} event={event} />;
    }

    if (changeType === 'ENRICHMENT_ACCEPTED') {
        return <TimelineBannerIntelligenceDetailsAdded key={event.id} event={event} />;
    }

    return <TimelineBannerCompanyType key={event.id} event={event} />;
}

export function getLeadBanner(input: {
    changeType: ChangeType;
    event: EventFragmentSparse;
    currentUser: SessionUser;
}) {
    const {changeType, event, currentUser} = input;

    // Possible lead changes that we display in the timeline
    const leadChange: Change | null | undefined =
        event.changes.find((change) => change.attribute === STATUS) ||
        event.changes.find((change) => change.attribute === PRIORITY) ||
        event.changes.find((change) => change.attribute === STAGE) ||
        event.changes.find((change) => change.attribute === VALUE);

    if (!leadChange || !leadChange.newValue) {
        // When a lead is opened, there is no change type
        if (changeType === 'ADDED') {
            return (
                <TimelineBannerLeadStatus
                    key={event.id}
                    event={event}
                    currentUser={currentUser}
                    newStatus={LEAD_OPENED_STATUS}
                />
            );
        }

        if (changeType === 'MERGED') {
            return <TimelineBannerEntityMerged key={event.id} event={event} />;
        }

        if (changeType === 'LEAD_CLONED') {
            return <TimelineBannerLeadCloned key={event.id} event={event} />;
        }

        if (changeType === 'EDITED' && leadChange && leadChange.attribute === VALUE) {
            return getValueBanner(event, leadChange);
        }

        return null;
    }

    switch (changeType) {
        case 'EDITED':
            if (leadChange.attribute === PRIORITY) {
                return (
                    <TimelineBannerLeadPriority
                        key={event.id}
                        event={event}
                        newPriority={leadChange.newValue}
                    />
                );
            }

            if (leadChange.attribute === VALUE) {
                return getValueBanner(event, leadChange);
            }

            return (
                <TimelineBannerLeadStatus
                    key={event.id}
                    event={event}
                    currentUser={currentUser}
                    newStatus={leadChange.newValue}
                />
            );
        case 'STAGE_CHANGED':
            return (
                <TimelineBannerStageChange
                    key={event.id}
                    event={event}
                    newStage={JSON.parse(leadChange.newValue).name}
                />
            );
        default:
            // Should not ever happen
            return null;
    }
}

export function getQuoteLinkText(htmlUrlPath: string, displayNumber: string) {
    return (
        <Link as={RoutedLink} to={htmlUrlPath} variant='primary'>
            <strong>{displayNumber}</strong>
        </Link>
    );
}

export function makeTargetEntitiesDisplay(
    targetEntities: Array<React.ReactElement<any>>,
    name: string,
    htmlUrl: string
) {
    return targetEntities.length > 0 ? (
        targetEntities.reduce((acc, entity, index) => {
            if (index === 0) {
                return entity;
            }

            return (
                <>
                    {acc}, {entity}
                </>
            );
        })
    ) : (
        <LinkedEntity
            type='leads'
            useEntityColor={true}
            shouldClientRoute={true}
            name={name}
            htmlUrl={htmlUrl}
            allowWrap={true}
            displayInline={true}
            greenLeads={true}
        />
    );
}

export function getTargetEntities(lead: Lead): Array<React.ReactElement<any>> {
    const targetEntities = [];

    if (lead.accounts && lead.accounts.primaryEdge) {
        const primaryAccount = lead.accounts.primaryEdge.node;
        targetEntities.push(
            <LinkedEntity
                type='accounts'
                useEntityColor={true}
                shouldClientRoute={true}
                name={primaryAccount.name}
                htmlUrl={primaryAccount.htmlUrl}
                allowWrap={true}
                displayInline={true}
            />
        );
    }

    if (lead.contacts && lead.contacts.primaryEdge) {
        const primaryContact = lead.contacts.primaryEdge.node;
        targetEntities.push(
            <LinkedEntity
                type='contacts'
                useEntityColor={true}
                shouldClientRoute={true}
                name={primaryContact.name}
                htmlUrl={primaryContact.htmlUrl}
                allowWrap={true}
                displayInline={true}
            />
        );
    }

    return targetEntities;
}

function getValueBanner(event: EventFragmentSparse, leadChange: Change) {
    const valueModeChange = event.changes.find((change) => change.attribute === VALUE_MODE);

    if (!valueModeChange || !valueModeChange.newValue) {
        return null;
    }

    return (
        <TimelineBannerLeadValue
            key={event.id}
            event={event}
            newValue={leadChange.newValue ?? null}
            newValueMode={valueModeChange.newValue}
        />
    );
}
